import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import AdminLayout, { ViewSiteButton } from '../../components/admin/AdminLayout';
import { FeaturedActivitiesManager } from '../../components/admin/FeaturedActivitiesManager';
import { FeaturedPicksManager } from '../../components/admin/FeaturedPicksManager';
import { useAuth } from '../../hooks/useAuth';
import { bilingualLinesJoined } from '../../i18n/script';
import { useSiteLanguage } from '../../i18n/useSiteLanguage';

interface DashboardStats {
  totalActivities: number;
  activeActivities: number;
  totalBookings: number;
  confirmedBookings: number;
  grossRevenue: number;
  upcomingSessions: number;
}

interface RecentBooking {
  id: string;
  activityTitle: string;
  activityTitleCN?: string | null;
  activityTitleEN?: string | null;
  customerName: string;
  customerEmail: string;
  totalAmount: number;
  status: string;
  createdAt: string;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const siteLanguage = useSiteLanguage();
  const { user, isAuthenticated, loading: authLoading, getToken } = useAuth();

  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentBookings, setRecentBookings] = useState<RecentBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check admin access
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
    // TODO: Check for admin role once role system is fully implemented
  }, [authLoading, isAuthenticated, navigate]);

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!isAuthenticated) return;

      try {
        setLoading(true);
        const token = await getToken();
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

        const response = await fetch(`${apiUrl}/admin/dashboard`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch dashboard data');
        }

        const result = await response.json();
        if (result.success) {
          setStats(result.data.stats);
          setRecentBookings(result.data.recentBookings || []);
        }
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load dashboard';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [isAuthenticated, getToken]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <AdminLayout title="Admin Dashboard" headerRight={<ViewSiteButton />}>
      <div className="mb-6 text-sm text-gray-500">Welcome, {user?.name || user?.email}</div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          {error}
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Active Activities"
          value={stats?.activeActivities ?? 0}
          subtitle={`${stats?.totalActivities ?? 0} total`}
          icon="📅"
        />
        <StatCard
          title="Confirmed Bookings"
          value={stats?.confirmedBookings ?? 0}
          subtitle={`${stats?.totalBookings ?? 0} total`}
          icon="✅"
        />
        <StatCard
          title="Upcoming Sessions"
          value={stats?.upcomingSessions ?? 0}
          subtitle="Next 7 days"
          icon="🗓️"
        />
        <StatCard
          title="Gross Revenue"
          value={`$${((stats?.grossRevenue ?? 0) / 100).toFixed(2)}`}
          subtitle="All Stripe income"
          icon="💰"
        />
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-4">
          <Link
            to="/admin/activities/new"
            className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          >
            <span className="mr-2">+</span>
            Create New Activity
          </Link>
          <Link
            to="/admin/bookings"
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            View All Bookings
          </Link>
          <Link
            to="/admin/activities"
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Manage Activities
          </Link>
          <Link
            to="/admin/passes"
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Manage Passes
          </Link>
        </div>
      </div>

      {/* Featured Activities Management */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Featured Activities</h2>
          <span className="text-sm text-gray-500">
            Select and order activities shown on homepage
          </span>
        </div>
        <FeaturedActivitiesManager getToken={getToken} />
      </div>

      {/* Featured YOYO Picks: the order of the homepage Picks rows */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Featured YOYO Picks</h2>
          <span className="text-sm text-gray-500">Select and order Picks shown on homepage</span>
        </div>
        <FeaturedPicksManager />
      </div>

      {/* Recent Bookings */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Recent Bookings</h2>
          <Link to="/admin/bookings" className="text-primary hover:underline text-sm">
            View all
          </Link>
        </div>
        <div className="overflow-x-auto">
          {recentBookings.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No bookings yet. Share your activities to get started!
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Activity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentBookings.map((booking) => {
                  const [titleTop, titleBottom] = bilingualLinesJoined(
                    [[booking.activityTitleCN, booking.activityTitleEN]],
                    siteLanguage,
                    booking.activityTitle
                  );
                  return (
                    <tr key={booking.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{titleTop}</div>
                        {titleBottom && <div className="text-xs text-gray-500">{titleBottom}</div>}
                        <div className="text-xs text-gray-500">
                          {new Date(booking.createdAt).toLocaleString('en-US', {
                            timeZone: 'America/Los_Angeles',
                            month: 'numeric',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true,
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{booking.customerName}</div>
                        <div className="text-sm text-gray-500">{booking.customerEmail}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${(booking.totalAmount / 100).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={booking.status} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

// Stat Card Component
function StatCard({
  title,
  value,
  subtitle,
  icon,
}: {
  title: string;
  value: string | number;
  subtitle: string;
  icon: string;
}) {
  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-2">
        <span className="text-2xl">{icon}</span>
      </div>
      <div className="text-2xl font-bold text-gray-900 mb-1">{value}</div>
      <div className="text-sm font-medium text-gray-900">{title}</div>
      <div className="text-xs text-gray-500">{subtitle}</div>
    </div>
  );
}

// Status Badge Component
function StatusBadge({ status }: { status: string }) {
  const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
    CONFIRMED: { bg: 'bg-green-100', text: 'text-green-800', label: 'Confirmed' },
    CANCELLED: { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelled' },
    CANCELLED_CREDIT: { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelled (Credit)' },
    CANCELLED_REFUND: { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelled (Refund)' },
    CANCELLED_PASS: { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelled (Pass)' },
    CANCELLED_BY_HOST: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Awaiting Refund' },
    REFUNDED: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Refunded' },
    TRANSFERRED: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Transferred' },
  };

  const config = statusConfig[status] || {
    bg: 'bg-gray-100',
    text: 'text-gray-800',
    label: status,
  };

  return (
    <span
      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.bg} ${config.text}`}
    >
      {config.label}
    </span>
  );
}
